import { useEffect, useMemo, useRef, useState } from "react";
import type { Comment, Id, ScriptColumn } from "@supermanus/document-model";

export type CommentMenuMode = "create-cell" | "create-range" | "list";

export interface CellCommentMenuProps {
  x: number;
  y: number;
  mode: CommentMenuMode;
  column: ScriptColumn;
  selectionQuote?: string;
  comments: Comment[];
  readOnly?: boolean;
  onAdd: (body: string) => void;
  onResolve: (commentId: Id) => void;
  onRemove: (commentId: Id) => void;
  onClose: () => void;
}

type CommentGroup = {
  key: string;
  kind: "cell" | "range";
  quote?: string;
  comments: Comment[];
};

function isRangeComment(c: Comment): boolean {
  return Boolean(c.quote) || (c.from != null && c.to != null && c.to > c.from);
}

function groupComments(comments: Comment[]): CommentGroup[] {
  const order: string[] = [];
  const map = new Map<string, CommentGroup>();

  for (const c of comments) {
    const range = isRangeComment(c);
    const key = range
      ? `range:${(c.quote ?? "").trim().toLowerCase() || `${c.from}-${c.to}`}`
      : "cell";
    let group = map.get(key);
    if (!group) {
      group = {
        key,
        kind: range ? "range" : "cell",
        quote: c.quote?.trim() || undefined,
        comments: [],
      };
      map.set(key, group);
      order.push(key);
    } else if (!group.quote && c.quote?.trim()) {
      group.quote = c.quote.trim();
    }
    group.comments.push(c);
  }

  return order.map((k) => map.get(k)!);
}

function CellAnchor({ column }: { column: ScriptColumn }) {
  const label = column === "visual" ? "Billedside" : "Lydside";
  return (
    <div
      className="sm-comment-anchor sm-comment-anchor-cell"
      title={`Kommentar til hele ${label.toLowerCase()}-cellen`}
      aria-label={`Celle: ${label}`}
    >
      <span
        className={`sm-comment-mini-col${column === "visual" ? " is-target" : ""}`}
        aria-hidden
      />
      <span
        className={`sm-comment-mini-col${column === "audio" ? " is-target" : ""}`}
        aria-hidden
      />
      <span className="sm-comment-anchor-caption">{label}</span>
    </div>
  );
}

function RangeAnchor({ quote }: { quote: string }) {
  const truncated =
    quote.length > 140 ? `${quote.slice(0, 140).trimEnd()}…` : quote;
  return (
    <div
      className="sm-comment-anchor sm-comment-anchor-range"
      title="Kommentar til markering i teksten"
      aria-label={`Markering: ${truncated}`}
    >
      <mark className="sm-comment-mark-preview">{truncated}</mark>
    </div>
  );
}

export function CellCommentMenu({
  x,
  y,
  mode,
  column,
  selectionQuote,
  comments,
  readOnly,
  onAdd,
  onResolve,
  onRemove,
  onClose,
}: CellCommentMenuProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: x, top: y });
  const [draft, setDraft] = useState("");
  const creating = mode === "create-cell" || mode === "create-range";
  const groups = useMemo(() => groupComments(comments), [comments]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPos({
      left: Math.min(x, window.innerWidth - rect.width - 8),
      top: Math.min(y, window.innerHeight - rect.height - 8),
    });
  }, [x, y, mode, comments.length, groups.length]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    function onPointer(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onPointer);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onPointer);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      className="sm-ctx-menu sm-comment-menu"
      style={{ left: pos.left, top: pos.top }}
      role="dialog"
      aria-label="Kommentar"
    >
      {creating && !readOnly ? (
        <div className="sm-comment-compose">
          {mode === "create-range" && selectionQuote ? (
            <RangeAnchor quote={selectionQuote} />
          ) : (
            <CellAnchor column={column} />
          )}
          <textarea
            className="sm-comment-draft"
            value={draft}
            autoFocus
            rows={3}
            placeholder="Skriv kommentar…"
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                const body = draft.trim();
                if (!body) return;
                onAdd(body);
                onClose();
              }
            }}
          />
          <div className="sm-comment-menu-actions">
            <button type="button" onClick={onClose}>
              Annuller
            </button>
            <button
              type="button"
              className="sm-comment-save"
              disabled={!draft.trim()}
              onClick={() => {
                const body = draft.trim();
                if (!body) return;
                onAdd(body);
                onClose();
              }}
            >
              Gem
            </button>
          </div>
        </div>
      ) : null}

      {groups.length > 0 ? (
        <div className={`sm-comment-groups${creating ? " is-below-compose" : ""}`}>
          {groups.map((group) => (
            <section key={group.key} className="sm-comment-group">
              {group.kind === "range" && group.quote ? (
                <RangeAnchor quote={group.quote} />
              ) : (
                <CellAnchor column={column} />
              )}
              <ul className="sm-comment-list">
                {group.comments.map((c, idx) => (
                  <li key={c.id}>
                    <div className="sm-comment-meta">
                      <strong>{c.authorName}</strong>
                      {group.comments.length > 1 ? (
                        <span className="sm-comment-thread-count">
                          {idx + 1}/{group.comments.length}
                        </span>
                      ) : null}
                    </div>
                    <p>{c.body}</p>
                    {!readOnly ? (
                      <div className="sm-comment-menu-actions">
                        <button
                          type="button"
                          onClick={() => onResolve(c.id)}
                          title="Skjul kommentaren som løst (kan findes under Løste kommentarer)"
                        >
                          Løst
                        </button>
                        <button
                          type="button"
                          onClick={() => onRemove(c.id)}
                          title="Slet kommentaren permanent"
                        >
                          Slet
                        </button>
                      </div>
                    ) : null}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      ) : !creating ? (
        <p className="sm-comment-empty">Ingen kommentarer</p>
      ) : null}
    </div>
  );
}
